import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { SessionManagerService, SessionWarningData } from '../../core/services/session-manager.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-session-warning-dialog',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="sg-dlg-backdrop" [class.visible]="visible">
      <div class="sg-dlg-panel" role="alertdialog" aria-modal="true" aria-labelledby="sg-sess-title">
        <div class="sg-dlg-eyebrow">{{ 'session.eyebrow' | translate }}</div>
        <h3 class="sg-dlg-title" id="sg-sess-title">{{ 'session.expiringTitle' | translate }}</h3>
        <p class="sg-dlg-message">{{ getWarningMessage() }}</p>

        <div class="sg-dlg-time" [class.critical]="remainingTime <= 60" aria-live="polite">
          <span class="value">{{ getFormattedRemainingTime() }}</span>
          <span class="label">{{ 'session.warnDialog.remaining' | translate }}</span>
        </div>

        <!-- Extender AVANZA (primario blanco); cerrar sesión es la salida secundaria. -->
        <div class="sg-dlg-actions">
          <button type="button" class="sgv2-btn-pill ghost" (click)="onCloseSession()">
            {{ 'session.warnDialog.logoutCta' | translate }}
          </button>
          <button type="button" class="sgv2-btn-pill primary" (click)="onExtendSession()">
            <i class="pi pi-refresh" aria-hidden="true"></i>{{ 'session.warnDialog.extendCta' | translate }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @use '../../features/alerts/styles/alerts-v2.tokens.scss' as *;

    // Modal canónico del sistema (sgv2-modal-backdrop / sgv2-modal-panel), como el resto de
    // diálogos. Antes: cabecera en degradado naranja, borde de 2px y botones verde/rojo llenos.
    .sg-dlg-backdrop {
      @include sgv2-modal-backdrop;
      position: fixed;
      inset: 0;
      z-index: 1100;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.2s ease, visibility 0.2s ease;

      &.visible { opacity: 1; visibility: visible; }
    }

    .sg-dlg-panel {
      @include sgv2-modal-panel;
      width: min(420px, 100%);
      padding: 24px 24px 20px;
      font-family: $sgv2-font-ui;
    }

    .sg-dlg-eyebrow {
      font-family: $sgv2-font-mono;
      font-size: 10px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: $sgv2-text-faint;
      margin-bottom: 8px;
    }

    .sg-dlg-title {
      margin: 0 0 10px;
      font-family: $sgv2-font-display;
      font-weight: 300;
      font-size: 24px;
      letter-spacing: -0.01em;
      color: $sgv2-text;
    }

    .sg-dlg-message {
      margin: 0 0 18px;
      font-size: 14px;
      line-height: 1.5;
      color: $sgv2-text-dim;
    }

    .sg-dlg-time {
      display: flex;
      align-items: baseline;
      gap: 8px;
      margin-bottom: 20px;

      .value {
        font-family: $sgv2-font-mono;
        font-size: 26px;
        color: $sgv2-sev-med;
        transition: color 0.2s ease;
      }
      .label { font-size: 12px; color: $sgv2-text-faint; }
      &.critical .value { color: $sgv2-sev-crit; }
    }

    .sg-dlg-bar {
      height: 3px;
      margin: -12px 0 20px;
      border-radius: $sgv2-r-pill;
      background: $sgv2-border-soft;
      overflow: hidden;

      span {
        display: block;
        height: 100%;
        background: $sgv2-text-dim;
        transition: width 1s linear;
      }
      &.critical span { background: $sgv2-sev-crit; }
    }

    .sg-dlg-actions {
      display: flex;
      justify-content: flex-end;
      flex-wrap: wrap;
      gap: 8px;
    }

    .sgv2-btn-pill { @include sgv2-btn-pill; }

    @media (prefers-reduced-motion: reduce) {
      .sg-dlg-backdrop, .sg-dlg-bar span { transition: none; }
    }
  `]
})
/**
 * Aviso de caducidad de sesión. Es una VISTA de `SessionManagerService.sessionWarning$`: el
 * servicio decide cuándo se muestra y lleva la cuenta atrás; aquí solo se pinta y se
 * devuelven las dos decisiones del usuario (extender / cerrar).
 *
 * Hasta el 25-10-2026 el componente arrastraba un segundo monitor de token propio
 * (`interval(30000)` + lecturas de claves legacy de localStorage que un parche de `main.ts`
 * bloqueaba), un `show()`/`hide()` con callbacks y una cuenta atrás paralela que nadie
 * arrancaba, además del anillo de progreso de un diseño anterior. Nada de eso se ejecutaba.
 */
export class SessionWarningDialogComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);

  visible = false;
  remainingTime = 30;

  private sessionSubscription?: Subscription;

  constructor(
    private readonly sessionManager: SessionManagerService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.sessionSubscription = this.sessionManager.sessionWarning$.subscribe((data: SessionWarningData) => {
      this.visible = data.show;
      this.remainingTime = data.remainingTime;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.sessionSubscription?.unsubscribe();
  }

  onExtendSession(): void {
    this.sessionManager.extendSession();
  }

  onCloseSession(): void {
    this.sessionManager.closeSession();
  }

  getWarningMessage(): string {
    if (this.remainingTime > 10) {
      return this.translate.instant('session.warnDialog.extend');
    } else if (this.remainingTime > 5) {
      return this.translate.instant('session.warnDialog.soon');
    } else {
      return this.translate.instant('session.warnDialog.seconds');
    }
  }

  /** Muestra minutos y segundos con formato "m:ss": "4:30", "4:29", "0:09". */
  getFormattedRemainingTime(): string {
    const total = Math.max(0, this.remainingTime);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}